import { AddOutlined } from '@mui/icons-material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { Alert, Box, CardHeader, Stack, Tooltip } from '@mui/material'
import type { GridRowParams } from '@mui/x-data-grid'
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from '@mui/x-data-grid-pro'
import { useQueryClient } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import DataGridDetailPanelToggle from '@/components/dataDisplay/DataGridDetailPanelToggle'
import CustomButton from '@/components/inputs/CustomButton'
import SteeringRangeDetails from '@/features/resource/components/steering/SteeringRangeDetails'
import SteeringRangeForm from '@/features/resource/components/steering/SteeringRangeForm'
import SteeringRangesTooltipContent from '@/features/resource/components/steering/SteeringRangesTooltipContent'
import { StyledCustomDataGrid } from '@/features/resource/components/steering/StyledCustomDataGrid'
import type { AddSteeringRangeRequest, UpdateSteeringRangeRequest } from '@/features/resource/endpoints/resources'
import { GET_RESOURCE_API_ID } from '@/features/resource/endpoints/resources'
import { useAddSteeringRangeMutation } from '@/features/resource/hooks/useAddSteeringRangeMutation'
import { useDeleteSteeringRangeMutation } from '@/features/resource/hooks/useDeleteSteeringRangeMutation'
import { useUpdateSteeringRangeMutation } from '@/features/resource/hooks/useUpdateSteeringRangeMutation'
import type {
  Resource,
  ResourceMultistepMapping,
  ResourceSteeringConfig,
  ResourceSteeringRange,
  ResourceSteeringRangeEntry,
} from '@/features/resource/types'
import { convertToKilo } from '@/features/resource/utils/convertToKilo'

export type ResourceSteeringRangesPanelProps = {
  resource: Resource
  steeringConfig: ResourceSteeringConfig | undefined
}

export interface SteeringRangesDataGridProps {
  isMultistepResource: boolean
  controlPorts: Resource['controlPorts']
  steeringRanges: ResourceSteeringRange[]
  onUpdateRange: (rangeId: string, data: UpdateSteeringRangeRequest) => void
  onDeleteRange: (rangeId: string) => void
}

const SteeringRangesDataGrid = ({
  isMultistepResource,
  controlPorts,
  steeringRanges,
  onUpdateRange,
  onDeleteRange,
}: SteeringRangesDataGridProps) => {
  const { t } = useTranslation()
  const [editingRange, setEditingRange] = useState<ResourceSteeringRange | null>(null)

  const convertToSteeringRangePattern = (steeringRange: ResourceSteeringRange): string => {
    const formatValue = (value: number | null) => {
      const kiloValue = convertToKilo(value)
      return kiloValue === null ? '-' : kiloValue.toString()
    }

    return `${formatValue(steeringRange.min.value)}; ${formatValue(steeringRange.max.value)}; ${formatValue(steeringRange.step.value)} kW`
  }

  const columns = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      renderCell: (params) => <DataGridDetailPanelToggle params={params} />,
      sortable: false,
      flex: 0.4,
    },
    {
      field: 'name',
      headerName: t('resources.steering.steering_ranges.name'),
      flex: 2,
      // Temporary solution until we save name of the steering range
      renderCell: (params) => `Steering_range_${params.api.getAllRowIds().indexOf(params.id) + 1}`,
    },
    {
      field: 'dateCreatedAt',
      headerName: t('resources.steering.steering_ranges.date_created'),
      flex: 2,
      valueFormatter: (value: string) => (value ? DateTime.fromISO(value).toFormat('dd MMMM yyyy, HH:mm') : '-'),
    },
    {
      field: 'pattern',
      headerName: t('resources.steering.steering_ranges.pattern'),
      flex: 2,
      valueGetter: (_, row) => convertToSteeringRangePattern(row),
    },
  ]

  return (
    <StyledCustomDataGrid
      disableColumnSorting
      hideFooter
      hideFooterPagination
      hideFooterRowCount
      columns={columns}
      getDetailPanelContent={({ row }: GridRowParams<ResourceSteeringRange>) =>
        editingRange?.id === row.id ? (
          <SteeringRangeForm
            controlPorts={controlPorts}
            initialData={row}
            isMultistepResource={isMultistepResource}
            steeringRanges={steeringRanges}
            onCancel={() => setEditingRange(null)}
            onDelete={() => onDeleteRange(row.id)}
            onSave={(data) => {
              onUpdateRange(row.id, data)
              setEditingRange(null)
            }}
          />
        ) : (
          <SteeringRangeDetails
            controlPorts={controlPorts}
            isMultistepResource={isMultistepResource}
            resourceSteeringRange={row}
            onEdit={() => setEditingRange(row)}
          />
        )
      }
      getDetailPanelHeight={() => 'auto'}
      getRowId={(row: ResourceSteeringRange) => row.id}
      includeWrapper={false}
      rows={steeringRanges}
    />
  )
}

const SteeringRangesPanel = ({ steeringConfig, resource }: ResourceSteeringRangesPanelProps) => {
  const { t } = useTranslation()
  const [isAddingNewRange, setIsAddingNewRange] = useState(false)
  const [localSteeringRanges, setLocalSteeringRanges] = useState<ResourceSteeringRange[]>([])
  const queryClient = useQueryClient()

  const { addSteeringRange } = useAddSteeringRangeMutation(resource.resourceID)
  const { updateSteeringRange } = useUpdateSteeringRangeMutation(resource.resourceID)
  const { deleteSteeringRange } = useDeleteSteeringRangeMutation(resource.resourceID)

  useEffect(() => {
    if (steeringConfig) {
      setLocalSteeringRanges(steeringConfig.steeringRanges)
    }
  }, [steeringConfig])

  const handleAddSteeringRange = async (data: AddSteeringRangeRequest) => {
    try {
      const tempId = 'temp-' + Date.now()
      const newRange: ResourceSteeringRange = {
        id: tempId,
        dateCreatedAt: new Date().toISOString(),
        max: { id: `${tempId}-max`, ...data.max },
        min: { id: `${tempId}-min`, ...data.min },
        step: { id: `${tempId}-step`, ...data.step },
        multistepMappings:
          data.multistepMappings?.map(
            (mapping) =>
              ({ controlPortId: mapping.controlPortId, stepValue: mapping.stepValue }) as ResourceMultistepMapping,
          ) ?? [],
      }
      setLocalSteeringRanges((prev) => [...prev, newRange])
      setIsAddingNewRange(false)

      await addSteeringRange(data)
      queryClient.invalidateQueries({ queryKey: [GET_RESOURCE_API_ID, { id: resource.resourceID }] })
    } catch (error) {
      console.error('Failed to add steering range:', error)
    }
  }

  const handleUpdateSteeringRange = async (rangeId: string, data: UpdateSteeringRangeRequest) => {
    try {
      setLocalSteeringRanges((prev) =>
        prev.map((range) => {
          if (range.id === rangeId) {
            const updateEntry = (
              current: ResourceSteeringRangeEntry,
              update: Partial<ResourceSteeringRangeEntry>,
            ): ResourceSteeringRangeEntry => ({
              ...current,
              ...update,
              id: current.id,
            })

            return {
              ...range,
              max: updateEntry(range.max, data.max),
              min: updateEntry(range.min, data.min),
              step: updateEntry(range.step, data.step),
              multistepMappings: data.multistepMappings || [],
            }
          }
          return range
        }),
      )

      await updateSteeringRange({ rangeId, data })
      queryClient.invalidateQueries({ queryKey: [GET_RESOURCE_API_ID, { id: resource.resourceID }] })
    } catch (error) {
      console.error('Failed to update steering range:', error)
    }
  }

  const handleDeleteSteeringRange = async (rangeId: string) => {
    try {
      setLocalSteeringRanges((prev) => prev.filter((range) => range.id !== rangeId))

      await deleteSteeringRange(rangeId)
      queryClient.invalidateQueries({ queryKey: [GET_RESOURCE_API_ID, { id: resource.resourceID }] })
    } catch (error) {
      console.error('Failed to delete steering range:', error)
    }
  }

  const handleAddSteeringRangeClick = () => setIsAddingNewRange(true)

  return (
    <Stack>
      <CardHeader
        title={
          <Stack alignItems="center" direction="row" spacing={1}>
            <CustomTypography variant="h6">{t('resources.steering.steering_ranges.label')}</CustomTypography>
            <Tooltip arrow placement="right-start" title={<SteeringRangesTooltipContent />}>
              <HelpOutlineIcon fontSize="small" sx={{ color: 'black' }} />
            </Tooltip>
          </Stack>
        }
      />
      {localSteeringRanges.length > 0 ? (
        <SteeringRangesDataGrid
          controlPorts={resource.controlPorts}
          isMultistepResource={steeringConfig?.isMultistepResource ?? false}
          steeringRanges={localSteeringRanges}
          onDeleteRange={handleDeleteSteeringRange}
          onUpdateRange={handleUpdateSteeringRange}
        />
      ) : (
        !isAddingNewRange && <Alert severity="info">{t('resources.steering.steering_ranges.info_empty')}</Alert>
      )}
      {isAddingNewRange && (
        <Box mt={2}>
          <SteeringRangeForm
            controlPorts={resource.controlPorts}
            isMultistepResource={steeringConfig?.isMultistepResource ?? false}
            steeringRanges={localSteeringRanges}
            onCancel={() => setIsAddingNewRange(false)}
            onSave={handleAddSteeringRange}
          />
        </Box>
      )}
      <Box display="flex" justifyContent="flex-start" mt={2}>
        <CustomButton
          disabled={isAddingNewRange}
          startIcon={<AddOutlined />}
          sx={{ mt: 2 }}
          variant="outlined"
          onClick={handleAddSteeringRangeClick}
        >
          {t('resources.steering.steering_ranges.add')}
        </CustomButton>
      </Box>
    </Stack>
  )
}

export default SteeringRangesPanel
